import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { clsx } from 'clsx';
import Button from 'components/Button/Button';
import Separator from 'components/Separator/Separator';
import { BUTTON_VARIANT } from 'constants/interfaces';

import Panel from '../../../../../components/Panel/Panel';
import CreateSurveyActionButtons from './components/CreateSurveyActionButtons';
import CreateSurveyPanel from './components/CreateSurveyPanel';
import CreateSurveyQuestion from './components/CreateSurveyQuestion';
import CreateSurveyStandards from './components/CreateSurveyStandards';

import type { ICreateSurveyProps } from './interfaces/ICreateSurvey';

import styles from './scss/CreateSurvey.module.scss';

function CreateSurveyView({
  control,
  onBack,
  onCancel,
  onSaveAsDraft,
  onMarkAsDone,
  isLoading,
  isSavingDraft,
  isSavingDone,
  fields,
  onRemoveQuestion,
  onAddQuestion,
  hasStandards,
  onAddStandards,
  onRemoveStandards,
  standardId,
  setValue,
  isViewMode,
}: ICreateSurveyProps) {
  const { t } = useTranslation();

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={styles.root}>
        <div className={styles.backNavigation}>
          <Button
            onClick={onBack}
            title={t('common.back')}
            startIcon={<ArrowLeftIcon />}
            className={styles.backButton}
            variant={BUTTON_VARIANT.ghost}
          />
        </div>
        <Panel className={clsx(styles.panel, { [styles.viewMode]: isViewMode })}>
          <div className={styles.wrapper}>
            <CreateSurveyPanel control={control} />
            {fields.map((field, index) => (
              <CreateSurveyQuestion
                control={control}
                index={index}
                key={field.id}
                onRemove={() => onRemoveQuestion(index)}
              />
            ))}
            {hasStandards ? (
              <CreateSurveyStandards
                control={control}
                standardId={standardId}
                setValue={setValue}
                onRemove={onRemoveStandards}
              />
            ) : null}
            {!isViewMode ? (
              <div className={styles.add}>
                <Button
                  title={t('materialityAssessment.addNewQuestion')}
                  startIcon={<PlusIcon />}
                  variant={BUTTON_VARIANT.ghost}
                  onClick={onAddQuestion}
                />
                {!hasStandards ? (
                  <Button
                    title={t('materialityAssessment.addPrioritizeStandards')}
                    startIcon={<PlusIcon />}
                    variant={BUTTON_VARIANT.ghost}
                    onClick={onAddStandards}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {!isViewMode ? (
            <>
              <Separator />
              <CreateSurveyActionButtons
                onCancel={onCancel}
                onSaveAsDraft={onSaveAsDraft}
                onMarkAsDone={onMarkAsDone}
                isLoading={isLoading}
                isSavingDraft={isSavingDraft}
                isSavingDone={isSavingDone}
              />
            </>
          ) : null}
        </Panel>
      </div>
    </DndProvider>
  );
}

export default CreateSurveyView;
